import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { sanitizeRichText } from '@/lib/sanitize';
import './RichTextEditor.css';
import { 
  Bold, Italic, List, ListOrdered, Quote,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Heading
} from 'lucide-react';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SimpleRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
}

export const SimpleRichTextEditor = ({ 
  value, 
  onChange, 
  placeholder,
  maxLength,
  disabled = false
}: SimpleRichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false, // Disable links
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Scrivi il tuo contenuto qui...',
      }),
      CharacterCount.configure({
        limit: maxLength,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Sanitize HTML before passing to parent
      const sanitizedHtml = sanitizeRichText(html);
      onChange(sanitizedHtml);
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  const characterCount = editor.storage.characterCount.characters();

  return (
    <div className={`border-[3px] border-dark ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      {/* Toolbar */}
      <TooltipProvider>
        <div className="flex flex-wrap gap-1 p-2 border-b-[3px] border-dark bg-muted/30">
          {/* Bold */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('bold') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleBold().run()}
                className="!p-2 transition-all"
              >
                <Bold className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Testo grassetto (Ctrl+B)</p>
            </TooltipContent>
          </Tooltip>

          {/* Italic */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('italic') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                className="!p-2 transition-all"
              >
                <Italic className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Testo corsivo (Ctrl+I)</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Heading */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('heading', { level: 2 }) ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className="!p-2"
              >
                <Heading className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Titolo di sezione (H2)</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Bullet List */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('bulletList') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className="!p-2"
              >
                <List className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Elenco puntato</p>
            </TooltipContent>
          </Tooltip>

          {/* Ordered List */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('orderedList') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className="!p-2"
              >
                <ListOrdered className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Elenco numerato</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Align Left */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                className="!p-2"
              >
                <AlignLeft className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Allinea a sinistra</p>
            </TooltipContent>
          </Tooltip>

          {/* Align Center */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                className="!p-2"
              >
                <AlignCenter className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Allinea al centro</p>
            </TooltipContent>
          </Tooltip>

          {/* Align Right */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                className="!p-2"
              >
                <AlignRight className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Allinea a destra</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Blockquote */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant={editor.isActive('blockquote') ? 'primary' : 'outline'}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                className="!p-2"
              >
                <Quote className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Citazione</p>
            </TooltipContent>
          </Tooltip>

          <div className="w-[2px] bg-dark mx-1" />

          {/* Undo */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant="outline"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                className="!p-2"
              >
                <Undo className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Annulla modifica</p>
            </TooltipContent>
          </Tooltip>

          {/* Redo */}
          <Tooltip>
            <TooltipTrigger asChild>
              <BrutalButton
                type="button"
                size="sm"
                variant="outline"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                className="!p-2"
              >
                <Redo className="w-4 h-4" />
              </BrutalButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Ripeti modifica</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="tiptap-editor bg-background"
      />

      {/* Character Count */}
      {maxLength && (
        <div className="flex items-center justify-end p-2 border-t-[3px] border-dark bg-muted/30 text-sm">
          <span className={characterCount > maxLength ? 'text-destructive font-medium' : 'text-muted-foreground'}>
            {characterCount}/{maxLength}
          </span>
        </div>
      )}
    </div>
  );
};
